export type MinorTabDefinition = {
  key: string;
  label: string;
};

/** Left-nav keys minors can never access. */
export const MINOR_BLOCKED_NAV_TABS = new Set([
  'verification',
  'trust-hub',
  'earn-credits',
  'donation-settings',
  'payments',
  'my-id-verification-requests',
]);

/** Experimental portal sections gated by admin visible-tabs for normal users. */
export const EXPERIMENTAL_NAV_TABS = new Set(['public-profiles', 'social-posts']);

export const MINOR_PROFILE_SUBTAB_KEYS = ['profile/your-profiles', 'profile/account-settings'] as const;

/** Tabs a parent may assign when editing minor settings (before admin gating). */
export const ALL_MINOR_CONFIGURABLE_TAB_DEFINITIONS: MinorTabDefinition[] = [
  { key: 'messages', label: 'Messages (Inbox/Sent)' },
  { key: 'address-book', label: 'Address Book' },
  { key: 'category-settings', label: 'Commercial Postage Rates' },
  { key: 'account-history', label: 'Account History' },
  { key: 'public-profiles', label: 'Public Profiles' },
  { key: 'social-posts', label: 'Social Posts' },
  { key: 'profile', label: 'My Profile (navigation)' },
  { key: 'profile/your-profiles', label: 'Your Profiles' },
  { key: 'profile/account-settings', label: 'Account Settings' },
];

/** Default minor tab set when the parent has not saved an explicit list. */
export const DEFAULT_MINOR_ALLOWED_TABS: string[] = [
  'messages',
  'address-book',
  'category-settings',
  'account-history',
  'profile',
  'profile/your-profiles',
  'profile/account-settings',
];

export function isTabVisibleToParent(
  tabKey: string,
  visibleTabKeys: string[],
  hasAdvancedMailbox: boolean,
): boolean {
  if (hasAdvancedMailbox) {
    return true;
  }

  if (tabKey === 'profile') {
    return (
      visibleTabKeys.length === 0 ||
      visibleTabKeys.includes('profile') ||
      MINOR_PROFILE_SUBTAB_KEYS.some((k) => visibleTabKeys.includes(k))
    );
  }

  if (EXPERIMENTAL_NAV_TABS.has(tabKey) || tabKey.startsWith('profile/')) {
    return visibleTabKeys.length === 0 || visibleTabKeys.includes(tabKey);
  }

  if (visibleTabKeys.length === 0) {
    return true;
  }

  return visibleTabKeys.includes(tabKey);
}

export function getConfigurableTabsForMinor(
  visibleTabKeys: string[],
  hasAdvancedMailbox: boolean,
): MinorTabDefinition[] {
  return ALL_MINOR_CONFIGURABLE_TAB_DEFINITIONS.filter((def) =>
    isTabVisibleToParent(def.key, visibleTabKeys, hasAdvancedMailbox),
  );
}

export function resolveMinorAllowedTabs(stored: string[] | null | undefined): string[] {
  if (stored != null && stored.length > 0) {
    return stored;
  }
  return [...DEFAULT_MINOR_ALLOWED_TABS];
}

/** Effective tab keys for a minor at runtime (defaults, parent list, admin gating). */
export function sanitizeMinorAllowedTabsForRuntime(
  stored: string[] | null | undefined,
  visibleTabKeys: string[],
  hasAdvancedMailbox: boolean,
): string[] {
  return resolveMinorAllowedTabs(stored).filter(
    (key) =>
      isTabVisibleToParent(key, visibleTabKeys, hasAdvancedMailbox) &&
      !MINOR_BLOCKED_NAV_TABS.has(key),
  );
}

export function isMinorNavTabVisible(tabKey: string, effectiveAllowedTabs: string[]): boolean {
  if (MINOR_BLOCKED_NAV_TABS.has(tabKey)) {
    return false;
  }
  return effectiveAllowedTabs.includes(tabKey);
}

export function initialMinorEditAllowedTabs(
  stored: string[] | null | undefined,
  configurableKeys: string[],
): string[] {
  const allowedSet = new Set(configurableKeys);
  if (stored != null && stored.length > 0) {
    return stored.filter((key) => allowedSet.has(key));
  }
  return DEFAULT_MINOR_ALLOWED_TABS.filter((key) => allowedSet.has(key));
}
